/**
 * Alarm rows -> map markers for the Alarms explore overlay.
 *
 * Mirrors `tagsExploreMapMarkers.ts`: a marker carries a TIME, never a position. Alarm rows have no
 * lat/lng at all (see `shared/utils/alarmStreams.js` ALARM_COLUMNS), so
 * `AlarmsExploreMarkersLayer` interpolates the boat position from the track at `atIso` when it
 * draws. `source` is carried so the fleet maps can interpolate each marker against the boat that
 * actually raised it rather than against a mixed-source track.
 */

/**
 * The Category values the writer emits, duplicated here on purpose: the server-side source of
 * truth is `shared/utils/alarmStreams.js`, which is CommonJS and outside every frontend path alias
 * (the frontend imports nothing from `shared/`). Matching is case-insensitive and anything
 * unrecognised falls back to `info`, so a new category added server-side degrades rather than
 * disappearing.
 */
const CATEGORY_PENALTY = 'Penalty';
const CATEGORY_WARNING = 'Warning';
const CATEGORY_INFO = 'Info';

/** Lower-cased category, used directly as the CSS modifier suffix. */
export type AlarmExploreMapMarkerCategory = 'penalty' | 'warning' | 'info';

export interface AlarmExploreMapMarker {
  /** Stable D3 join key. */
  id: string;
  /** ISO timestamp for track interpolation. */
  atIso: string;
  /** Boat that raised the alarm; empty on a single-source page. */
  source: string;
  category: AlarmExploreMapMarkerCategory;
  tooltip: string;
}

/** One alarm row as the page holds it, before it becomes a marker. */
export interface AlarmMarkerRowInput {
  /** Epoch SECONDS — the alarms parquet has no Datetime column. */
  ts: number | string | null | undefined;
  source?: string | null;
  category?: string | null;
  description?: string | null;
}

/** Anything unrecognised renders as info rather than vanishing. */
export function resolveAlarmCategory(category: string | null | undefined): AlarmExploreMapMarkerCategory {
  const c = String(category ?? '').trim().toLowerCase();
  if (c === CATEGORY_PENALTY.toLowerCase()) {return 'penalty';}
  if (c === CATEGORY_WARNING.toLowerCase()) {return 'warning';}
  if (c === CATEGORY_INFO.toLowerCase()) {return 'info';}
  return 'info';
}

/**
 * Epoch seconds -> ISO. Tolerates a string `ts` (Arrow can hand back either) and returns null for
 * anything unparseable so the caller can skip the row instead of drawing it at the epoch.
 */
export function alarmTsToIso(ts: number | string | null | undefined): string | null {
  if (ts === null || ts === undefined || ts === '') {return null;}
  const n = typeof ts === 'number' ? ts : Number(ts);
  if (!Number.isFinite(n)) {return null;}
  // Seconds by contract, but tolerate a millisecond value rather than plotting it in 1970.
  const ms = n < 1e12 ? n * 1000 : n;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Build the overlay's markers from the rows the table is currently showing. */
export function buildAlarmExploreMapMarkers(rows: AlarmMarkerRowInput[]): AlarmExploreMapMarker[] {
  const out: AlarmExploreMapMarker[] = [];
  const seen = new Map<string, number>();
  rows.forEach((row) => {
    const atIso = alarmTsToIso(row.ts);
    if (!atIso) {return;}
    const category = resolveAlarmCategory(row.category);
    const source = String(row.source ?? '').trim();
    const description = String(row.description ?? '').trim();
    const tooltip = description
      ? (source ? `${description}\n${source}` : description)
      : (source || category);
    // STABLE, CONTENT-DERIVED KEY - not the row index.
    //
    // The layer keys its D3 join on this. An index-based id changes for every marker as soon as one
    // row enters or leaves the set, which the live time-window clip does constantly, so the join
    // would re-enter the whole overlay several times a second and visibly flicker. Derived from the
    // content instead, a marker keeps its identity across redraws and only its position updates.
    // The counter disambiguates the rare genuine duplicate (same boat, instant and text).
    const base = `${source}|${atIso}|${category}|${description}`;
    const dup = seen.get(base) ?? 0;
    seen.set(base, dup + 1);

    out.push({
      id: dup === 0 ? base : `${base}#${dup}`,
      atIso,
      source,
      category,
      tooltip,
    });
  });
  return out;
}
